import { useCallback, useEffect, useState } from 'react';
import { Button, Grid, MenuItem, TextField, Typography } from '@material-ui/core';
import { InfoCard, Link, StatusError, StatusOK, StatusPending, WarningPanel } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';

type Request = {
  id: string;
  environment: string;
  schedule: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  requested_by: string;
  note?: string;
  created_at: string;
  freeze_start?: string;
  freeze_end?: string;
};
type Active = { id: string; environment: string; description: string; created_by: string; created_at: string; starts_at?: string };
type State = { isDevops: boolean; runsUrl: string; active: Active[]; requests: Request[] };

const EMPTY = { environment: 'PROD', start: '', end: '', reason: '' };
const who = (ref: string) => ref.replace(/^user:default\//, '');
const when = (d: string) => new Date(d).toLocaleString('es-EC', { dateStyle: 'medium', timeStyle: 'short' });
const STATUS = {
  pending: <StatusPending>Pendiente de devops</StatusPending>,
  approved: <StatusOK>Aprobado y programado</StatusOK>,
  rejected: <StatusError>Rechazado</StatusError>,
};

/** Freeze de QA/PROD programado (inicio y fin, hora Ecuador): devops lo programa, cancela o termina (eks-freeze.yml); el resto lo solicita. */
export const FreezeSection = ({ onChange }: { onChange: () => void }) => {
  const discovery = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const [state, setState] = useState<State>();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const call = useCallback(
    async (path: string, body?: unknown) => {
      const res = await fetch(`${await discovery.getBaseUrl('devops-news')}${path}`, {
        method: body === undefined ? 'GET' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text();
        let message = text || `HTTP ${res.status}`;
        try {
          const parsed = JSON.parse(text);
          message = parsed.error?.message ?? parsed.error ?? message;
        } catch {
          // respuesta sin JSON
        }
        throw new Error(message);
      }
      return res.status === 204 ? undefined : res.json();
    },
    [discovery, fetch],
  );
  const load = useCallback(() => call('/freeze').then(setState), [call]);
  useEffect(() => {
    load().catch(e => setError(String(e?.message ?? e)));
  }, [load]);

  const run = async (path: string, body: unknown, confirmText?: string) => {
    // eslint-disable-next-line no-alert
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true);
    setError(undefined);
    try {
      await call(path, body);
      setForm(EMPTY);
      await load();
      onChange();
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setBusy(false);
    }
  };

  if (!state) return error ? <WarningPanel title="No se pudo cargar el freeze" message={error} /> : null;
  const set = (key: keyof typeof EMPTY) => (e: any) => setForm({ ...form, [key]: e.target.value });

  return (
    <InfoCard title="Freeze" subheader="Bloquea los despliegues en QA o PROD (variable de la organización, anuncio en GitHub y pipeline de Azure DevOps)">
      {error && <WarningPanel title="Algo falló" message={error} />}
      {state.active.map(f => {
        const scheduled = !!f.starts_at && new Date(f.starts_at) > new Date();
        return (
          <Typography key={f.id} style={{ marginBottom: 8 }}>
            {scheduled ? (
              <StatusPending>
                <b>Freeze programado en {f.environment}</b> ({who(f.created_by)}): {f.description}
              </StatusPending>
            ) : (
              <StatusError>
                <b>Freeze activo en {f.environment}</b> desde {when(f.starts_at ?? f.created_at)} ({who(f.created_by)}): {f.description}
              </StatusError>
            )}
            {state.isDevops && (
              <Button
                size="small"
                variant="outlined"
                disabled={busy}
                style={{ marginLeft: 8 }}
                onClick={() => run(`/freeze/${f.id}/stop`, {}, scheduled ? `¿Cancelar el freeze programado en ${f.environment}?` : `¿Lanzar UNFREEZE en ${f.environment}?`)}
              >
                {scheduled ? 'Cancelar' : 'Terminar (UNFREEZE)'}
              </Button>
            )}
          </Typography>
        );
      })}

      <Grid container spacing={2}>
        <Grid item md={2} xs={12}>
          <TextField select fullWidth label="Ambiente" value={form.environment} onChange={set('environment')}>
            <MenuItem value="QA">QA</MenuItem>
            <MenuItem value="PROD">PROD</MenuItem>
          </TextField>
        </Grid>
        <Grid item md={3} xs={12}>
          <TextField fullWidth type="datetime-local" label="Inicio (hora Ecuador)" InputLabelProps={{ shrink: true }} value={form.start} onChange={set('start')} />
        </Grid>
        <Grid item md={3} xs={12}>
          <TextField fullWidth type="datetime-local" label="Fin (hora Ecuador)" InputLabelProps={{ shrink: true }} value={form.end} onChange={set('end')} />
        </Grid>
        <Grid item md={4} xs={12}>
          <TextField fullWidth label="Motivo" value={form.reason} onChange={set('reason')} />
        </Grid>
        <Grid item xs={12}>
          {state.isDevops ? (
            <Button
              variant="contained"
              color="secondary"
              disabled={busy || !form.reason || !form.start || !form.end}
              onClick={() => run('/freeze/start', form, `¿Programar FREEZE en ${form.environment} de ${form.start.replace('T', ' ')} a ${form.end.replace('T', ' ')}?`)}
            >
              Programar freeze
            </Button>
          ) : (
            <Button variant="contained" color="primary" disabled={busy || !form.reason || !form.start || !form.end} onClick={() => run('/freeze/requests', form)}>
              Solicitar freeze a devops
            </Button>
          )}
          <Link to={state.runsUrl} style={{ marginLeft: 16 }}>Ver ejecuciones del workflow</Link>
        </Grid>
      </Grid>

      {state.requests.length > 0 && (
        <>
          <Typography variant="subtitle2" style={{ marginTop: 16 }}>
            {state.isDevops ? 'Solicitudes pendientes' : 'Tus solicitudes'}
          </Typography>
          {state.requests.map(r => (
            <div key={r.id} style={{ padding: '6px 0', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
              <span>
                <b>{r.environment}</b> · {r.schedule} · {r.reason} · {who(r.requested_by)} · {when(r.created_at)}
              </span>
              {state.isDevops ? (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={busy}
                    onClick={() =>
                      run(
                        '/freeze/start',
                        { environment: r.environment, start: r.freeze_start, end: r.freeze_end, reason: r.reason, requestId: r.id },
                        `¿Aprobar y programar FREEZE en ${r.environment} (${r.schedule})?`,
                      )
                    }
                  >
                    Aprobar y programar
                  </Button>
                  <Button
                    size="small"
                    disabled={busy}
                    onClick={() => {
                      // eslint-disable-next-line no-alert
                      const note = window.prompt('Motivo del rechazo (opcional)') ?? '';
                      run(`/freeze/requests/${r.id}/reject`, { note });
                    }}
                  >
                    Rechazar
                  </Button>
                </>
              ) : (
                <>
                  {STATUS[r.status]}
                  {r.note && <span>({r.note})</span>}
                </>
              )}
            </div>
          ))}
        </>
      )}
    </InfoCard>
  );
};
